import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { type Concepto, getTelemetriaHoraria } from "@/lib/api-edoctas";
import { formatMXN } from "@/lib/utils";

type Props = {
  conceptos: Concepto[];
  selectedFul: string | null;
  onSelectFul: (ful: string) => void;
};

export function TelemetriaHorariaTab({ conceptos, selectedFul, onSelectFul }: Props) {
  // Autoseleccionar el primer concepto cuando entras al tab.
  useEffect(() => {
    if (!selectedFul && conceptos.length > 0) {
      onSelectFul(conceptos[0].ful);
    }
  }, [conceptos, selectedFul, onSelectFul]);

  const tele = useQuery({
    queryKey: ["telemetria-horaria", selectedFul],
    queryFn: () => getTelemetriaHoraria(selectedFul!),
    enabled: Boolean(selectedFul),
    retry: false,
  });

  const chartData = useMemo(() => {
    if (!tele.data) return [];
    // Pivotear a [{ hora: 1, [elemento1]: monto, [elemento2]: monto, ... }, ...]
    const byHora = new Map<number, Record<string, number | string>>();
    for (let h = 1; h <= 24; h++) {
      byHora.set(h, { hora: h });
    }
    for (const r of tele.data.registros) {
      const row = byHora.get(r.hora);
      if (row) row[r.elemento] = Number(r.monto_horario ?? 0);
    }
    return Array.from(byHora.values());
  }, [tele.data]);

  const selectedConcepto = conceptos.find((c) => c.ful === selectedFul);

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4">
        <label className="block text-xs font-medium text-slate-600 mb-1">
          Concepto
        </label>
        <select
          className="border rounded px-2 py-1.5 w-full max-w-xl"
          value={selectedFul ?? ""}
          onChange={(e) => onSelectFul(e.target.value)}
        >
          {conceptos.length === 0 && <option value="">— Sin conceptos —</option>}
          {conceptos.map((c) => (
            <option key={c.ful} value={c.ful}>
              {c.grupo ?? "?"} — {c.descripcion ?? c.ful}
            </option>
          ))}
        </select>
        {selectedConcepto && (
          <div className="mt-2 text-xs text-slate-500">
            Total neto del concepto:{" "}
            <span className="font-medium text-slate-700">
              {formatMXN(selectedConcepto.total_neto)}
            </span>
          </div>
        )}
      </div>

      {tele.isLoading && <div className="text-slate-500">Cargando telemetría…</div>}
      {tele.isError && (
        <div className="bg-white border rounded-lg px-4 py-8 text-center text-slate-500">
          Este concepto no tiene telemetría horaria.
        </div>
      )}

      {tele.data && (
        <>
          <div className="bg-white border rounded-lg p-4">
            <div className="text-sm font-medium mb-2">Monto horario por elemento</div>
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="hora" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) =>
                      new Intl.NumberFormat("es-MX", {
                        notation: "compact",
                        compactDisplay: "short",
                      }).format(Number(v))
                    }
                  />
                  <Tooltip
                    formatter={(v: number) => formatMXN(v)}
                    labelFormatter={(h) => `Hora ${h}`}
                  />
                  <Legend />
                  {tele.data.elementos.map((el, i) => (
                    <Bar key={el} dataKey={el} stackId="a" fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b text-sm font-medium">
              Detalle horario ({tele.data.registros.length} registros)
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Hora</th>
                    <th className="px-3 py-2 font-medium">Elemento</th>
                    <th className="px-3 py-2 font-medium">Nodo</th>
                    <th className="px-3 py-2 font-medium text-right">Potencia (MDA)</th>
                    <th className="px-3 py-2 font-medium text-right">Potencia (MTR)</th>
                    <th className="px-3 py-2 font-medium text-right">Precio</th>
                    <th className="px-3 py-2 font-medium text-right">Monto horario</th>
                  </tr>
                </thead>
                <tbody>
                  {tele.data.registros.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-1.5">{r.hora}</td>
                      <td className="px-3 py-1.5">{r.elemento}</td>
                      <td className="px-3 py-1.5 text-slate-500">{r.nodo ?? "—"}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {r.potencia_mda ?? "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {r.potencia_mtr ?? "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {r.precio ?? "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {formatMXN(r.monto_horario)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const PALETTE = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#db2777",
  "#65a30d",
];
